<template>
    <div class="container mx-auto px-4 pb-12">
        <!-- Loading State -->
        <div v-if="loading" class="flex justify-center items-center min-h-screen">
            <div class="text-center">
                <CSpinner size="large" />
                <p class="mt-4 text-gray-600 dark:text-txt-body">Načítavam článok...</p>
            </div>
        </div>

        <!-- Error State -->
        <!-- Independent v-ifs, not a v-else-if chain: a draft is filled in after mount, and a
             chain would patch the article into the error container (CLAUDE.md, SSR pitfalls). -->
        <div v-if="!loading && !showPost" class="max-w-2xl mx-auto text-center py-16">
            <h1 class="text-4xl font-bold text-gray-900 mb-4 dark:text-txt-headings">Článok nenájdený</h1>
            <p class="text-gray-600 mb-8 dark:text-txt-body">Ľutujeme, ale tento článok neexistuje alebo bol odstránený.</p>
            <NuxtLink :to="blogListingLink" class="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 font-medium dark:text-primary-300 dark:hover:text-primary-300">
                <svg class="size-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                </svg>
                Späť na blog
            </NuxtLink>
        </div>

        <!-- Blog Post Content -->
        <div v-if="!loading && showPost && post">
            <PreviewBanner
                v-if="post.preview"
                :id="post.id"
                class="mx-auto mb-6 max-w-4xl rounded-lg border"
                :can-share="post.preview.canShare"
                label="tento článok ešte nie je zverejnený."
                type="blog"
            />

            <!-- Breadcrumbs and Actions -->
            <div class="mx-auto mb-6 flex max-w-4xl flex-wrap items-center justify-between gap-x-4 gap-y-2">
                <nav class="min-w-0 flex-1 text-sm">
                    <ol class="flex items-center gap-2 text-gray-600 dark:text-txt-body">
                        <li><NuxtLink to="/" class="hover:text-primary-600 dark:hover:text-primary-300">Domov</NuxtLink></li>
                        <li>/</li>
                        <li><NuxtLink :to="blogListingLink" class="hover:text-primary-600 dark:hover:text-primary-300">Blog</NuxtLink></li>
                        <li>/</li>
                        <li class="min-w-0 truncate font-medium text-gray-900 dark:text-txt-headings">{{ post.title }}</li>
                    </ol>
                </nav>

                <!-- Edit & Delete Buttons (visible to author or @leaf.sk users) -->
                <div v-if="canDelete" class="flex items-center gap-2">
                    <NuxtLink
                        v-if="canEdit"
                        :to="{ name: 'my-blog-edit-id', params: { id: post.id } }"
                        class="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-colors dark:text-primary-300 dark:hover:text-primary-300 dark:hover:bg-primary-900/30"
                    >
                        <svg class="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        Upraviť
                    </NuxtLink>
                    <button
                        type="button"
                        class="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors dark:text-red-300 dark:hover:text-red-300 dark:hover:bg-red-900/30"
                        :disabled="isDeleting"
                        @click="handleDeleteClick"
                    >
                        <svg class="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        {{ isDeleting ? 'Mazanie...' : 'Vymazať' }}
                    </button>
                </div>
            </div>

            <!-- Post Content -->
            <BlogPostContent :post="post" />
            <BlogNextStep v-if="!post.preview" :post="post" />
            <BlogEngagement v-if="!post.preview" :key="post.id" :slug="post.slug" :post-id="post.id" />

            <!-- Related Posts -->
            <div v-if="relatedPosts.length > 0" class="mt-16">
                <h2 class="text-2xl font-bold text-gray-900 mb-8 dark:text-txt-headings">Podobné články</h2>
                <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <BlogPostCard
                        v-for="relatedPost in relatedPosts"
                        :key="relatedPost.id"
                        :post="relatedPost"
                    />
                </div>
            </div>

            <!-- Back to Blog -->
            <div class="mt-12 text-center">
                <NuxtLink
                    :to="blogListingLink"
                    class="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 font-medium dark:text-primary-300 dark:hover:text-primary-300"
                >
                    <svg class="size-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                    </svg>
                    Všetky články
                </NuxtLink>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import type { BlogPost } from '#shared/types/Blog'
import { blogDate } from '#shared/utils/blog'
import { useBlogSearch } from '@/composables/blog/useBlogSearch'
import { useJsonLd } from '@/composables/head/useJsonLd'
import { useApplicationStore } from '@/stores/application'
import PreviewBanner from '@/components/common/PreviewBanner.vue'
import {
    absoluteUrl,
    buildBreadcrumbList,
    GROWNI_ORGANIZATION,
    SECTION_SOCIAL_IMAGES,
    withSiteName
} from '@/utils/structuredData'

/** API extras: `preview` is set for unpublished posts the requester may see. */
type BlogPostResponse = BlogPost & { preview?: { canShare: boolean } | null }
type BlogPostApiResult = BlogPostResponse | { locked: true }

const route = useRoute()
const router = useRouter()
// Deleting an article needs an author/admin token — plain $fetch sends none.
const { authFetch } = useAuthFetch()
const { getRelatedPosts } = useBlogSearch()

const applicationStore = useApplicationStore()
const { showError } = useToast()

const previewToken = computed(() => (typeof route.query.preview === 'string' ? route.query.preview : undefined))

function getFreshQueryValue(): string | undefined {
    const fresh = route.query.fresh
    if (Array.isArray(fresh)) {
        return fresh.find((value): value is string => typeof value === 'string' && value.length > 0)
    }
    return typeof fresh === 'string' && fresh.length > 0 ? fresh : undefined
}

function getSlugParam(): string {
    const slug = route.params.slug
    return Array.isArray(slug) ? slug.join('/') : String(slug || '')
}

const blogDataKey = computed(() => {
    const fresh = getFreshQueryValue()
    const slug = getSlugParam()
    return fresh ? `blog-post-${slug}-${fresh}` : `blog-post-${slug}`
})

const blogListingLink = computed(() => {
    const fresh = getFreshQueryValue()
    return fresh ? { path: '/blog', query: { fresh } } : '/blog'
})

// Load blog post using server API (works on SSR)
const { data: blogData, status, error: blogError } = await useAsyncData(
    blogDataKey,
    async () => {
        const slug = getSlugParam()

        // Missing articles and upstream failures must retain their HTTP status during SSR.
        // Only related recommendations are optional; their failure cannot hide an article.
        const loadedPost = await $fetch<BlogPostApiResult>(`/api/blogs/${encodeURIComponent(slug)}`, {
            query: { preview: previewToken.value }
        })
        if (!loadedPost) {
            throw createError({ statusCode: 404, statusMessage: 'Article not found' })
        }
        // SSR has no auth; authors retry unpublished posts on the client.
        if ('locked' in loadedPost) {
            return { post: null, relatedPosts: [], locked: true }
        }
        let related: BlogPost[] = []
        try {
            related = await getRelatedPosts(loadedPost, 3)
        } catch (error) {
            console.error('[blog/[slug]] Error loading related posts:', error)
        }
        return { post: loadedPost, relatedPosts: related }
    },
    {
        watch: [() => route.params.slug, () => route.query.fresh] // Refetch when slug/fresh changes
    }
)

// Real 404/500 like the other detail pages, not a "Článok nenájdený" page with status 200.
// Locked drafts are not errors: SSR has no auth, so their author unlocks them on the client.
if (blogError.value) {
    const isMissing = blogError.value.statusCode === 404
    throw createError({
        statusCode: blogError.value.statusCode || 500,
        statusMessage: isMissing ? 'Článok nenájdený' : 'Článok sa nepodarilo načítať',
        fatal: true
    })
}

const post = computed<BlogPostResponse | null>(() => blogData.value?.post || null)
const relatedPosts = computed(() => blogData.value?.relatedPosts || [])
const loading = computed(() => status.value === 'pending' || isUnlocking.value)

// Access control for unpublished posts lives in the API (preview link, author or admin).
// SSR carries no auth, so a locked post is retried here with the user's ID token.
// Starts true for a locked post so SSR renders the spinner rather than "Článok nenájdený".
const isUnlocking = ref(!!blogData.value?.locked)
onMounted(async () => {
    if (!blogData.value?.locked) return
    try {
        const result = await authFetch<BlogPostApiResult>(`/api/blogs/${encodeURIComponent(getSlugParam())}`, {
            query: { preview: previewToken.value }
        })
        if (!('locked' in result)) {
            blogData.value = { post: result, relatedPosts: [] }
        }
    } catch (unlockError) {
        console.error('[blog/[slug]] Failed to load unpublished post:', unlockError)
    } finally {
        isUnlocking.value = false
    }
})

// Computed to check if we should show the post
const showPost = computed(() => !!post.value)

// Article visits are measured by the consent-gated Cheese page tracker.

// Editing is author/admin-only; verified LEAF staff can also delete via the API.
const canEdit = computed(() => !!post.value && (
    applicationStore.userId === post.value.author?.id || applicationStore.APPLICATION_USER_IS_ADMIN
))
const canDelete = computed(() => canEdit.value || !!(
    post.value && applicationStore.firebaseUser?.emailVerified &&
    applicationStore.firebaseUser.email?.toLowerCase().endsWith('@leaf.sk')
))

// Delete state
const isDeleting = ref(false)

async function handleDeleteClick() {
    if (!post.value) return

    const confirmed = window.confirm(`Naozaj chcete vymazať článok "${post.value.title}"? Táto akcia je nevratná.`)
    if (!confirmed) return

    isDeleting.value = true
    try {
        // Use server-side API for reliable deletion
        await authFetch(`/api/blogs/${encodeURIComponent(post.value.slug)}`, {
            method: 'DELETE'
        })
        router.push('/blog')
    } catch (err) {
        console.error('Failed to delete blog post:', err)
        showError('Nepodarilo sa vymazať článok')
    } finally {
        isDeleting.value = false
    }
}

// Helper to get ISO date string from various date formats
function getISODate(date: unknown): string {
    return blogDate(date)?.toISOString() || ''
}

const articleTitle = computed(() => post.value?.metaTitle || post.value?.title || '')
const articleDescription = computed(() => post.value?.metaDescription || post.value?.excerpt || '')
const articleImage = computed(() => post.value?.featuredImage || SECTION_SOCIAL_IMAGES.blog)
const articleUrl = computed(() => absoluteUrl(`/blog/${post.value?.slug || getSlugParam()}`))
// Same link as the author box in BlogPostContent.
const authorUrl = computed(() => {
    const authorId = post.value?.author?.id
    return authorId ? absoluteUrl(`/u/${encodeURIComponent(authorId)}`) : undefined
})

// https://developers.google.com/search/docs/appearance/structured-data/article
useJsonLd(() => {
    const current = post.value
    if (!current || current.preview) return []

    const publishedTime = getISODate(current.publishedAt)
    return [
        {
            '@type': 'BlogPosting',
            headline: current.title,
            description: articleDescription.value,
            image: articleImage.value,
            datePublished: publishedTime,
            dateModified: getISODate(current.updatedAt) || publishedTime,
            author: current.author?.name
                ? { '@type': 'Person', name: current.author.name, url: authorUrl.value }
                : GROWNI_ORGANIZATION,
            publisher: GROWNI_ORGANIZATION,
            keywords: current.tags?.join(', '),
            inLanguage: 'sk',
            mainEntityOfPage: { '@type': 'WebPage', '@id': articleUrl.value }
        },
        buildBreadcrumbList([
            { name: 'Domov', path: '/' },
            { name: 'Blog', path: '/blog' },
            { name: current.title, path: articleUrl.value }
        ])
    ]
})

// Dynamic page meta
useHead(() => {
    if (!post.value) {
        return {
            title: withSiteName(isUnlocking.value ? 'Blog' : 'Článok nenájdený'),
            meta: [{ name: 'robots', content: 'noindex, follow' }]
        }
    }

    const publishedTime = getISODate(post.value.publishedAt)
    const modifiedTime = getISODate(post.value.updatedAt)
    const title = withSiteName(articleTitle.value)

    return {
        title,
        link: [
            // `key` dedupes against the site-wide canonical set in app.vue
            { rel: 'canonical', key: 'canonical', href: articleUrl.value }
        ],
        meta: [
            ...(post.value.preview ? [{ name: 'robots', content: 'noindex, nofollow' }] : []),
            { name: 'description', content: articleDescription.value },
            { property: 'og:title', content: title },
            { property: 'og:description', content: articleDescription.value },
            { property: 'og:image', content: articleImage.value },
            { property: 'og:image:alt', content: post.value.featuredImageAlt || post.value.title },
            { property: 'og:type', content: 'article' },
            { property: 'og:url', content: articleUrl.value },
            { property: 'og:site_name', content: 'GROWNi' },
            { property: 'article:author', content: post.value.author?.name || '' },
            { property: 'article:published_time', content: publishedTime },
            { property: 'article:modified_time', content: modifiedTime || publishedTime },
            { name: 'twitter:card', content: 'summary_large_image' },
            { name: 'twitter:title', content: title },
            { name: 'twitter:description', content: articleDescription.value },
            { name: 'twitter:image', content: articleImage.value }
        ]
    }
})
</script>
